/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import assign from 'lodash/assign';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import get from 'lodash/get';
import keys from 'lodash/keys';
import {
    IS_INPUT_VALID,
    IS_INPUT_ARRAY_VALID,
    CLEAR_VALIDATION_ERROR,
    RESET_VALIDATION_ERRORS,
} from '../constants';

const INITIALSTATE = {
    errors: {},
    firstError: '',
};

/**
 * @param {object} st current state in store
 * Search and find the first nested error key in state, and compile a "firstErrorId" using the error key
 * and the error id (and nestedCampsiteId if the first-level error entry have nested campsite property)
 */
const getFirstErrorInState = (st) => {
    let firstErrorId = '';
    if (!isEmpty(st.errors)) {
        // Find the first id instance in state
        const firstId = keys(st.errors)[0];
        // Find the first error key instance inside the first id instance in state
        const firstErrorKey = keys(get(st.errors, firstId, {}))[0];
        // Get the resulting first error object
        const nestedEntry = get(st.errors, `${firstId}.${firstErrorKey}`, {});

        /* If the first error object does not contain a props entry, that means there might be another level of error properties
            - Find the first property id in the nested error object
            - Compile the firstErrorId using keys from 3 levels
        */
        if (isNil(nestedEntry.props)) {
            const nestedCampsiteId = keys(nestedEntry)[0];
            firstErrorId = !isNil(firstErrorKey)
                ? `${firstId}${firstErrorKey}${nestedCampsiteId}`
                : '';
        } else {
            /* Else, compile the firstErrorId using keys from 2 levels */
            firstErrorId = !isNil(firstErrorKey) ? `${firstErrorKey}${firstId}` : '';
        }
    }
    return firstErrorId;
};

const updateErrorState = (state, payload) => {
    const st = state;
    /* Reset the current validation's campsite and error keys in the 'errors' state
    if the validation's campsite entry already contains errors */
    if (st.errors[payload.id]) {
        // Delete validation's error entry if exists
        delete st.errors[payload.id][payload.errorName];
        // If after deletion, the campsite entry is free of errors, remove validation's campsite entry
        if (isEmpty(st.errors[payload.id])) {
            delete st.errors[payload.id];
        }
    }
    /* If current validation is erroneous, insert error */
    if (payload.error) {
        set(st, `errors[${payload.id}][${payload.errorName}]`, payload.error);
    }
    /* Update first error in state */
    st.firstError = getFirstErrorInState(st);
    return st;
};

const updateArrayErrorState = (state, payload) => {
    const st = state;
    const { id, campsiteId, errorName, error } = payload;
    /* Reset the current validation's campsite and error keys in the 'errors' state
    if the validation's campsite entry already contains errors */
    if (!isNil(get(st, `errors[${id}][${errorName}][${campsiteId}]`, null))) {
        // Delete validation's error entry if exists
        delete st.errors[id][errorName][campsiteId];
        // If after deletion, iteratively delete error levels if the entries are empty
        if (isEmpty(st.errors[id][errorName])) {
            delete st.errors[id][errorName];
            if (isEmpty(st.errors[id])) {
                delete st.errors[id];
            }
        }
    }
    /* If current validation is erroneous, insert error */
    if (error) {
        if (isNil(st.errors[id])) {
            assign(st.errors, {
                [id]: { [errorName]: { [campsiteId]: error } },
            });
        } else if (isNil(st.errors[id][errorName])) {
            assign(st.errors[id], {
                [errorName]: { [campsiteId]: error },
            });
            set(st, `errors.${id}.${errorName}.${campsiteId}`, error);
        } else {
            st.errors[id][errorName][campsiteId] = error;
        }
    }
    /* Update first error in state */
    st.firstError = getFirstErrorInState(st);

    return st;
};

const orderDetailsValidations = (state = INITIALSTATE, action) => {
    const st = { ...state };
    switch (action.type) {
        /* ================================= */
        /*           Form Validation         */
        /* ================================= */
        case IS_INPUT_VALID:
            return updateErrorState(st, action.payload);

        case IS_INPUT_ARRAY_VALID:
            return updateArrayErrorState(st, action.payload);

        case CLEAR_VALIDATION_ERROR:
            if (st.errors[action.payload.campsiteId]) {
                delete st.errors[action.payload.campsiteId];
            }
            return st;
        case RESET_VALIDATION_ERRORS:
            return {
                errors: {},
                firstError: '',
            };
        default:
            return st;
    }
};

export default orderDetailsValidations;
